import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ItemContent, Virtuoso, Components, VirtuosoHandle } from "react-virtuoso";
import { ItemListRowRenderer } from "@/components/item-list/rows";
import { ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { css } from "@/domains/emotion";
import { ItemListSkeleton } from "@/components/item-list/item-list-skeleton";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { useTheme } from "@emotion/react";
import { useHotkey } from "@tanstack/react-hotkeys";
import { isEditableElement } from "@/modules/html";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";
import {
  blurInteractiveElement,
  getNextHighlightedItemId,
  getRowIndexByItemId,
  getScrollIntoViewLocation,
  suppressNextInteractiveClick,
} from "@/modules/keyboard-shortcuts/virtualized-item-list-navigation";
import type { HighlightDirection } from "@/modules/keyboard-shortcuts/virtualized-item-list-navigation";

interface VirtualizedItemListProps {
  enableArrowHighlightNavigation?: boolean;
  endReached?: () => void;
  listState?: ListStateObservable;
  loading?: boolean;
  onOpenHighlightedItem?: (args: { itemId: string; event?: KeyboardEvent }) => void;
  rows: ItemListRowData[];
  scrollTop?: number;
  setScrollTop?: (scrollTop: number) => void;
}

const rowTypesWithBorders: (keyof typeof ItemListRowType)[] = [
  // ItemListRowType.Item
];
const Item: Components<ItemListRowData, HTMLDivElement>["Item"] = props => {
  const defaultClassName = "row";
  const extraClassName = rowTypesWithBorders.includes(props.item.type) ? "row-with-border" : "";
  const className = `${defaultClassName} ${extraClassName}`;

  return (
    <div className={className} {...props}>
      {props.children}
    </div>
  );
};

export const VirtualizedItemList: FC<VirtualizedItemListProps> = observer(
  ({
    enableArrowHighlightNavigation = false,
    endReached,
    listState,
    loading = false,
    onOpenHighlightedItem,
    rows,
    scrollTop = 0,
    setScrollTop,
  }) => {
    const { store } = useAppStore();
    const maybeTab = useMaybeTabContext();
    const virtuosoRef: React.MutableRefObject<VirtuosoHandle | null> = useRef(null);
    const previousOrderedItemIdsSignatureRef = useRef<string>();
    const keyboardNavigationStartItemIdRef = useRef<string>();
    const { isVisible, onScroll } = useVirtuoso(virtuosoRef, scrollTop, setScrollTop);
    const highlightedItemId = listState?.highlightedItemId;
    const isOwningActivePane =
      !maybeTab || maybeTab.activeTab.paneId === store.navigation.tabs?.activePaneId;
    const hotkeyTarget = typeof window === "undefined" ? null : window;
    const orderedItemIdsSource = listState?.listStateProvider?.orderedItemIds;
    const orderedItemIds = useMemo(() => orderedItemIdsSource ?? [], [orderedItemIdsSource]);
    const orderedItemIdsSignature = useMemo(() => orderedItemIds.join("\0"), [orderedItemIds]);
    const rowIndexByItemId = useMemo(
      () => getRowIndexByItemId({ orderedItemIds, rows }),
      [orderedItemIds, rows]
    );

    useEffect(() => {
      if (!enableArrowHighlightNavigation || !listState) {
        previousOrderedItemIdsSignatureRef.current = orderedItemIdsSignature;
        return;
      }

      // ignore the first render
      if (previousOrderedItemIdsSignatureRef.current === undefined) {
        previousOrderedItemIdsSignatureRef.current = orderedItemIdsSignature;
        return;
      }

      if (previousOrderedItemIdsSignatureRef.current !== orderedItemIdsSignature) {
        // clear stale selection when the list data changes
        listState.clearSelection();
        listState.highlightItem();
        // restart keyboard navigation from the top
        keyboardNavigationStartItemIdRef.current = orderedItemIds[0];
      }

      previousOrderedItemIdsSignatureRef.current = orderedItemIdsSignature;
    }, [enableArrowHighlightNavigation, listState, orderedItemIds, orderedItemIdsSignature]);

    // should we use new "smooth render" hook?
    const useSmoothScroll = typeof scrollTop === "number" && !!setScrollTop;

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_toggle_highlighted_selection),
      () => {
        if (!listState || !highlightedItemId) return;
        listState.toggleItemSelection(highlightedItemId);
      },
      {
        enabled: isOwningActivePane && !!highlightedItemId && listState?.canSelect,
        target: hotkeyTarget,
      }
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_extend_selection_to_highlighted),
      () => {
        if (!listState || !highlightedItemId) return;
        listState.selectItemsFromLastSelectionUntil(highlightedItemId);
      },
      {
        enabled: isOwningActivePane && !!highlightedItemId && listState?.canSelect,
        target: hotkeyTarget,
      }
    );

    const handleArrowNavigation = useCallback(
      (direction: HighlightDirection, event?: KeyboardEvent) => {
        if (!listState || !enableArrowHighlightNavigation || orderedItemIds.length === 0) {
          return;
        }

        // leave text inputs alone
        if (isEditableElement(document.activeElement)) {
          return;
        }

        blurInteractiveElement(document.activeElement);

        const nextItemId = getNextHighlightedItemId({
          direction,
          fallbackHighlightedItemId: keyboardNavigationStartItemIdRef.current,
          highlightedItemId: listState.highlightedItemId,
          orderedItemIds,
        });

        if (!nextItemId) {
          return;
        }

        // move the keyboard highlight
        listState.highlightItem(nextItemId);
        const rowIndex = rowIndexByItemId.get(nextItemId);
        if (typeof rowIndex === "number") {
          // keep the highlighted row visible
          virtuosoRef.current?.scrollIntoView(
            getScrollIntoViewLocation({
              direction,
              rowIndex,
            })
          );
        }

        event?.preventDefault();
      },
      [enableArrowHighlightNavigation, listState, orderedItemIds, rowIndexByItemId]
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_previous_result),
      event => {
        handleArrowNavigation("up", event);
      },
      {
        enabled: isOwningActivePane && enableArrowHighlightNavigation && orderedItemIds.length > 0,
        target: hotkeyTarget,
      }
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_highlight_previous_vim),
      event => {
        handleArrowNavigation("up", event);
      },
      {
        enabled: isOwningActivePane && enableArrowHighlightNavigation && orderedItemIds.length > 0,
        target: hotkeyTarget,
      }
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_next_result),
      event => {
        handleArrowNavigation("down", event);
      },
      {
        enabled: isOwningActivePane && enableArrowHighlightNavigation && orderedItemIds.length > 0,
        target: hotkeyTarget,
      }
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_highlight_next_vim),
      event => {
        handleArrowNavigation("down", event);
      },
      {
        enabled: isOwningActivePane && enableArrowHighlightNavigation && orderedItemIds.length > 0,
        target: hotkeyTarget,
      }
    );

    const handleOpenHighlightedItem = useCallback(
      (event?: KeyboardEvent) => {
        if (!onOpenHighlightedItem || !listState?.highlightedItemId) {
          return;
        }

        if (isEditableElement(document.activeElement)) {
          return;
        }

        suppressNextInteractiveClick(document.activeElement);
        blurInteractiveElement(document.activeElement);
        onOpenHighlightedItem({ itemId: listState.highlightedItemId, event });
        event?.preventDefault();
        event?.stopPropagation?.();
      },
      [listState, onOpenHighlightedItem]
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_open_highlighted),
      handleOpenHighlightedItem,
      {
        enabled: isOwningActivePane && !!onOpenHighlightedItem && !!highlightedItemId,
        target: hotkeyTarget,
      }
    );

    useHotkey(
      resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.notes_list_open_highlighted_in_split_view),
      handleOpenHighlightedItem,
      {
        enabled: isOwningActivePane && !!onOpenHighlightedItem && !!highlightedItemId,
        target: hotkeyTarget,
      }
    );

    const itemContent: ItemContent<ItemListRowData, unknown> = useCallback(
      (i, data) => ItemListRowRenderer({ index: i, data }),
      []
    );

    const handleMouseLeave = useCallback(() => listState?.clearHoveredItem(), [listState]);
    const handleVirtuosoRef = useCallback(
      (virtuoso: VirtuosoHandle | null) => {
        // keep both scroll systems in sync during the migration
        virtuosoRef.current = virtuoso;
        store.navigation.setVirtuoso(virtuoso);
      },
      [store.navigation]
    );

    const theme = useTheme();
    const listStyles = css({
      scrollbarWidth: "none",
      transition: "opacity 0.1s ease-in-out",
      "::-webkit-scrollbar": {
        display: "none",
      },

      // Hack to solve flickering when cursor is right between rows.
      ".row": {
        borderTop: "1px solid transparent",
      },

      ".row-with-border + .row-with-border": {
        borderTopStyle: "solid",
        borderTopWidth: 1,
        borderTopColor: theme.colors.border.default,
      },
    });

    const showSkeleton = !!loading;
    if (showSkeleton) {
      return <ItemListSkeleton />;
    }

    if (useSmoothScroll) {
      return (
        <Virtuoso
          className={listStyles}
          ref={handleVirtuosoRef}
          onScroll={onScroll}
          totalCount={rows.length}
          data={rows}
          itemContent={itemContent}
          endReached={endReached}
          onMouseLeave={handleMouseLeave}
          components={{ Item }}
          style={{ opacity: isVisible ? 1 : 0 }}
          initialScrollTop={scrollTop}
        />
      );
    }

    // TODO: deprecate navigation store after migrating all other lists to useVirtuoso
    return (
      <Virtuoso
        className={listStyles}
        ref={handleVirtuosoRef}
        restoreStateFrom={store.navigation.restoreVirtuosoStateFrom}
        totalCount={rows.length}
        data={rows}
        itemContent={itemContent}
        endReached={endReached}
        onMouseLeave={handleMouseLeave}
        components={{ Item }}
      />
    );
  }
);

/**
 * Custom hook to manage Virtuoso list scrolling behavior and visibility transitions
 *
 * @param scrollTop - Current scroll position of the list
 * @param setScrollTop - Callback to update scroll position externally
 *
 * @returns Object containing:
 *   - onScroll: Scroll handler that updates external scroll position
 *   - isVisible: Boolean indicating if list should be visible (used for mount transition)
 */
const useVirtuoso = (
  virtuoso: React.MutableRefObject<VirtuosoHandle | null>,
  scrollTop: number,
  setScrollTop?: (scrollTop: number) => void
) => {
  const [isVisible, setIsVisible] = useState(false);

  const onScroll = () => {
    virtuoso.current?.getState(({ scrollTop }) => {
      setScrollTop?.(scrollTop);
    });
  };

  // special handler that effectively resets scroll to 0 when after-mount change detected
  useEffect(() => {
    if (!setScrollTop) return;
    if (scrollTop !== 0) return;

    virtuoso.current?.scrollToIndex({
      index: 0,
      align: "start",
      behavior: "auto",
    });
  }, [scrollTop, setScrollTop, virtuoso]);

  // this lil rascal is used to smooth the "mount" transition and masks content flashing
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  return { onScroll, isVisible };
};
